// Opérations immuables sur le contenu d'une proposition : { meta, slides: [{ id, blocks: [...] }] }.
import { newNodeId } from "../../../utils/offerCalc";

export const BLOCK_TYPES = [
  { type: "text", label: "Texte" },
  { type: "columns", label: "Colonnes" },
  { type: "image", label: "Image ou illustration" },
  { type: "lot_price", label: "Pied de lot (délai et prix)" },
  { type: "quote", label: "Devis" },
  { type: "table", label: "Tableau" },
  { type: "twin", label: "Jumeau 3D (page HTML)" },
];

// Une présentation n'a ni prix ni conditions : pas de pied de lot, de devis ni de page « bon pour accord ».
const NOT_IN_PRESENTATION = new Set(["lot_price", "quote", "conditions"]);
export const blockTypesFor = (document) => BLOCK_TYPES.filter((item) => document !== "presentation" || !NOT_IN_PRESENTATION.has(item.type));
export const layoutsFor = (document) => LAYOUTS.filter((item) => document !== "presentation" || !NOT_IN_PRESENTATION.has(item.layout));

export const LAYOUTS = [
  { layout: "content", label: "Contenu" },
  { layout: "visual", label: "Contenu + visuel" },
  { layout: "cover", label: "Couverture" },
  { layout: "conditions", label: "Conditions et bon pour accord" },
];

export const newBlock = (type, lots = []) => {
  const id = newNodeId("b");
  switch (type) {
    case "columns":
      return { id, type, style: "plain", arrows: false, items: [{ label: "Travaux", text: "- …" }, { label: "Livrables", text: "- …" }] };
    case "image":
      return { id, type, image: null, caption: "", height: 260 };
    case "lot_price":
      return { id, type, lot_id: lots[0]?.id || "lot", delay_label: "", note: "" };
    case "quote":
      return { id, type, intro: "", lot_ids: null, show_total: false, footer: "" };
    case "table":
      return { id, type, rows: [["Colonne 1", "Colonne 2"], ["", ""]] };
    case "twin":
      return { id, type, embed: null, poster: null, caption: "", height: 360 };
    default:
      return { id, type: "text", label: "", text: "", style: "plain" };
  }
};

export const newSlide = (layout = "content") => ({
  id: newNodeId("s"),
  layout,
  title: layout === "conditions" ? "Conditions commerciales et bon pour accord" : "",
  subtitle: "",
  tag: "",
  hidden: false,
  blocks: ["content", "visual"].includes(layout) ? [newBlock("text")] : [],
  ...(layout === "visual" ? { visual: { image: null, caption: "" }, visual_side: "right" } : {}),
});

const mapSlide = (content, slideId, fn) => ({
  ...content,
  slides: content.slides.map((slide) => (slide.id === slideId ? fn(slide) : slide)),
});

const move = (list, index, delta) => {
  const target = index + delta;
  if (index < 0 || target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

const apply = (value, patch) => (typeof patch === "function" ? patch(value) : { ...value, ...patch });

export const updateMeta = (content, patch) => ({ ...content, meta: apply(content.meta, patch) });

export const updateSlide = (content, slideId, patch) => mapSlide(content, slideId, (slide) => apply(slide, patch));

export const insertSlide = (content, afterId, slide) => {
  const index = content.slides.findIndex((item) => item.id === afterId);
  const slides = [...content.slides];
  slides.splice(index < 0 ? slides.length : index + 1, 0, slide);
  return { ...content, slides };
};

export const moveSlide = (content, slideId, delta) => ({
  ...content, slides: move(content.slides, content.slides.findIndex((slide) => slide.id === slideId), delta),
});

export const removeSlide = (content, slideId) => ({
  ...content, slides: content.slides.filter((slide) => slide.id !== slideId),
});

export const duplicateSlide = (content, slideId) => {
  const source = content.slides.find((slide) => slide.id === slideId);
  if (!source) return content;
  const copy = { ...source, id: newNodeId("s"), blocks: (source.blocks || []).map((block) => ({ ...block, id: newNodeId("b") })) };
  return insertSlide(content, slideId, copy);
};

export const updateBlock = (content, slideId, blockId, patch) => mapSlide(content, slideId, (slide) => ({
  ...slide, blocks: slide.blocks.map((block) => (block.id === blockId ? apply(block, patch) : block)),
}));

export const addBlock = (content, slideId, block, afterId) => mapSlide(content, slideId, (slide) => {
  const blocks = [...(slide.blocks || [])];
  const index = blocks.findIndex((item) => item.id === afterId);
  blocks.splice(index < 0 ? blocks.length : index + 1, 0, block);
  return { ...slide, blocks };
});

export const moveBlock = (content, slideId, blockId, delta) => mapSlide(content, slideId, (slide) => ({
  ...slide, blocks: move(slide.blocks, slide.blocks.findIndex((block) => block.id === blockId), delta),
}));

export const removeBlock = (content, slideId, blockId) => mapSlide(content, slideId, (slide) => ({
  ...slide, blocks: slide.blocks.filter((block) => block.id !== blockId),
}));

export const duplicateBlock = (content, slideId, blockId) => {
  const slide = content.slides.find((item) => item.id === slideId);
  const block = slide?.blocks.find((item) => item.id === blockId);
  return block ? addBlock(content, slideId, { ...block, id: newNodeId("b") }, blockId) : content;
};

export const updateItem = (block, index, patch) => ({
  ...block, items: block.items.map((item, position) => (position === index ? { ...item, ...patch } : item)),
});

export const updateCell = (block, row, column, value) => ({
  ...block,
  rows: block.rows.map((cells, r) => (r === row ? cells.map((cell, c) => (c === column ? value : cell)) : cells)),
});
